import React, { useRef } from 'react';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  type DataTableColumnDef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { useDialog } from '~/components/Dialog/Provider';
import {
  PaginatedDataTable,
  type PaginatedDataTableRef,
  StatusBadge,
  getTimestampColumn,
  useTimeFormat,
} from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import { WebhookRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

export const PageWebhooks: React.FC = () => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('webhooks');
  const tableRef = useRef<PaginatedDataTableRef>(null);

  const handleDelete = (entry: any) => {
    openAlert({
      headline: 'Delete webhook',
      content: 'This action cannot be undone.',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (!currentUser) {
          return;
        }
        WebhookRepository.delete(entry.id, entry.type === 'Sandbox')
          .then(() => {
            addSuccessNotification({
              content: `${entry.url} deleted`,
            });
            tableRef.current?.refetch();
          })
          .catch((error) => {
            addDangerNotification({
              content: error.message,
            });
          });
      },
    });
  };

  const handleFetch = async (params: any) => {
    return WebhookRepository.getAllList(params).then((response) => {
      const entries = response.webhookEndpoints.map((entry: any) => {
        const editPath = `${ROUTE.PLATFORM_WEBHOOKS}/edit/${entry.type}/${entry.id}`;

        return {
          id: entry.id,
          url: entry.url,
          type: entry.type,
          status: entry.isDisabled ? 'Disabled' : 'Active',
          created: entry.createdAt,
          action: (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="icon-xs" variant="muted" aria-label="Options">
                  <Icon.Dots />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={4} className="min-w-36">
                <DropdownMenuItem onSelect={() => navigate(editPath)}>Edit</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => handleDelete(entry)}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        };
      });
      return { entries, hasMore: false };
    });
  };

  const columns: DataTableColumnDef<Record<string, any>>[] = [
    {
      header: 'URL',
      accessor: 'url',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <span className="whitespace-nowrap">{value.replace('https://', '')}</span>,
    },
    {
      header: 'Type',
      width: 'minmax(min-content, 140px)',
      cell: ({ value }) => (
        <StatusBadge>
          {value === 'Production' ? <Icon.CircleCheck /> : <Icon.Sandbox />}
          {value}
        </StatusBadge>
      ),
      accessor: 'type',
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 'minmax(min-content, 140px)',
      cell: ({ value }) =>
        value === 'Disabled' ? (
          <StatusBadge tone="danger">Disabled</StatusBadge>
        ) : (
          <StatusBadge tone="info">Active</StatusBadge>
        ),
    },
    getTimestampColumn<Record<string, any>>({ accessor: 'created', timeFormat, onTimeFormatChange, width: '160px' }),
    {
      header: '',
      width: 'min-content',
      accessor: 'action',
      // The menu is portaled, but React still bubbles its clicks through here to the row.
      cell: ({ value }) => (
        <div className="flex" onClick={(event) => event.stopPropagation()}>
          {value}
        </div>
      ),
    },
  ];

  const createButton = (
    <Button size="sm" onClick={() => navigate(`${ROUTE.PLATFORM_WEBHOOKS}/edit`)}>
      <Icon.Plus />
      Create Webhook
    </Button>
  );

  return (
    <>
      <PageActions>{createButton}</PageActions>

      <PaginatedDataTable<Record<string, any>>
        ref={tableRef}
        tableId="webhooks"
        fetch={handleFetch}
        empty={{
          icon: <Icon.AnimationCode />,
          title: 'No webhooks',
          description: 'Add your first webhook to get started.',
          action: createButton,
        }}
        columns={columns}
        onRowClick={(row) => {
          navigate(`${ROUTE.PLATFORM_WEBHOOKS}/edit/${row.type}/${row.id}`);
        }}
      />
    </>
  );
};
